import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, addDays } from "date-fns";
import {
  ChevronLeft, Star, Clock, Users, MapPin, Check,
  CalendarIcon, CheckCircle2, CreditCard, ArrowRight,
  Sparkles,
} from "lucide-react";
import { Calendar } from "@/components/ui/calender";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EXPERIENCES } from "@/data/experienceData";

const PAYMENT_METHODS = ["Credit Card", "PayPal", "Apple Pay", "Google Pay"];

const STEPS = ["Details", "Checkout", "Confirmed"];

export default function ExperienceDetail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("id");
  const exp = EXPERIENCES.find((e) => e.id === id);

  const [view, setView] = useState("detail");
  const [selectedDate, setSelectedDate] = useState(/** @type {Date|undefined} */ (undefined));
  const [numPlayers, setNumPlayers] = useState(2);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [reservation, setReservation] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const AnyPopoverContent = /** @type {any} */ (PopoverContent);

  if (!exp) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Experience not found.</p>
          <button onClick={() => navigate("/exqura-experience")} className="text-[#D4AF37] font-semibold hover:underline">
            Back to Experiences
          </button>
        </div>
      </div>
    );
  }

  const total = exp.price * numPlayers;
  const stepIdx = STEPS.indexOf(view === "detail" ? "Details" : view === "checkout" ? "Checkout" : "Confirmed");

  const handleConfirm = async () => {
    if (!paymentMethod) return;
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setReservation("EXQ-" + Math.random().toString(36).substring(2, 8).toUpperCase());
    setIsLoading(false);
    setView("confirmation");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Stepper bar ── */}
      <div className="bg-[#0B2421] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={() => view === "detail" ? navigate("/exqura-experience") : setView(view === "checkout" ? "detail" : "checkout")}
            className="mr-3 text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          {STEPS.map((step, i) => (
            <div key={step} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i < stepIdx ? "bg-emerald-400 text-white" :
                  i === stepIdx ? "bg-[#D4AF37] text-[#0B2421]" :
                  "bg-white/15 text-white/40"
                }`}>
                  {i < stepIdx ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className={`text-[10px] mt-0.5 font-semibold whitespace-nowrap ${
                  i === stepIdx ? "text-[#D4AF37]" : i < stepIdx ? "text-emerald-400" : "text-white/30"
                }`}>{step}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px mx-2 mb-4 ${i < stepIdx ? "bg-emerald-400" : "bg-white/15"}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">

        {/* ══════════ DETAIL VIEW ══════════ */}
        {view === "detail" && (
          <motion.div key="detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

            {/* Hero */}
            <div className="relative h-72 sm:h-96 overflow-hidden">
              <img src={exp.image} alt={exp.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2421] via-[#0B2421]/30 to-transparent" />
              {exp.badge && (
                <span className={`absolute top-4 left-4 ${exp.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                  {exp.badge}
                </span>
              )}
              <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-8 pb-6">
                <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-1">EXQURA Experience</p>
                <h1 className="text-white font-bold text-3xl sm:text-4xl mb-1">{exp.name}</h1>
                <p className="text-white/70 text-sm">{exp.tagline}</p>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 text-[#D4AF37] fill-[#D4AF37]" />
                    <span className="text-white text-sm font-semibold">{exp.rating}</span>
                    <span className="text-white/50 text-xs">({exp.reviews} reviews)</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/60 text-xs">
                    <Clock className="w-3.5 h-3.5" /> {exp.duration}
                  </div>
                  <div className="flex items-center gap-1 text-white/60 text-xs">
                    <Users className="w-3.5 h-3.5" /> {exp.players}
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">

              {/* ── Left: content ── */}
              <div className="lg:col-span-2 space-y-6">

                {/* Location */}
                <div className="flex items-start gap-2 text-gray-500 text-sm">
                  <MapPin className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                  <span>{exp.location}</span>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-100 shadow-sm">
                  {["overview", "schedule", "includes"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize transition-all ${
                        activeTab === tab
                          ? "bg-[#0B2421] text-[#D4AF37]"
                          : "text-gray-400 hover:text-[#0B2421]"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  {activeTab === "overview" && (
                    <motion.div key="ov" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h3 className="text-[#0B2421] font-bold text-base mb-3">About This Experience</h3>
                        <p className="text-gray-500 text-sm leading-relaxed mb-5">{exp.description}</p>
                        <h4 className="text-[#0B2421] font-bold text-sm mb-3">Highlights</h4>
                        <div className="space-y-2">
                          {exp.highlights.map((h, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <div className="w-5 h-5 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-[#D4AF37]" />
                              </div>
                              <span className="text-gray-600 text-sm">{h}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "schedule" && (
                    <motion.div key="sc" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h3 className="text-[#0B2421] font-bold text-base mb-4">Day Schedule</h3>
                        <div className="space-y-0">
                          {exp.schedule.map((item, i) => (
                            <div key={i} className="flex gap-4">
                              {/* Timeline line */}
                              <div className="flex flex-col items-center">
                                <div className="w-8 h-8 rounded-full bg-[#0B2421] flex items-center justify-center shrink-0">
                                  <span className="text-[#D4AF37] text-[10px] font-bold">{i + 1}</span>
                                </div>
                                {i < exp.schedule.length - 1 && (
                                  <div className="w-px flex-1 bg-gray-200 my-1" />
                                )}
                              </div>
                              <div className={`pb-5 ${i === exp.schedule.length - 1 ? "pb-0" : ""}`}>
                                <p className="text-[#D4AF37] text-xs font-bold mb-0.5">{item.time}</p>
                                <p className="text-[#0B2421] font-semibold text-sm mb-0.5">{item.title}</p>
                                <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "includes" && (
                    <motion.div key="inc" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h3 className="text-[#0B2421] font-bold text-base mb-4">What's Included</h3>
                        <div className="space-y-3">
                          {exp.includes.map((item, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                              <div className="w-7 h-7 rounded-full bg-[#0B2421] flex items-center justify-center shrink-0">
                                <Check className="w-3.5 h-3.5 text-[#D4AF37]" />
                              </div>
                              <span className="text-[#0B2421] font-semibold text-sm">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* ── Right: booking panel ── */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-24">
                  <div className="mb-4">
                    <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-0.5">From</p>
                    <p className="text-[#0B2421] font-bold text-3xl">
                      SGD {exp.price}
                      <span className="text-sm text-gray-400 font-normal"> per player</span>
                    </p>
                  </div>

                  {/* Date picker */}
                  <div className="mb-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Select Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="w-full flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 text-sm hover:border-[#0B2421] transition-colors text-left">
                          <CalendarIcon className="h-4 w-4 text-[#D4AF37] shrink-0" />
                          <span className={selectedDate ? "text-gray-700" : "text-gray-400"}>
                            {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Pick a date"}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <AnyPopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={(d) => d < addDays(new Date(), 1)}
                        />
                      </AnyPopoverContent>
                    </Popover>
                  </div>

                  {/* Players */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Guests</label>
                    <div className="flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2">
                      <Users className="h-4 w-4 text-[#D4AF37]" />
                      <span className="flex-1 text-sm text-gray-600">{numPlayers} guest{numPlayers > 1 ? "s" : ""}</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setNumPlayers((n) => Math.max(1, n - 1))}
                          className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold"
                        >−</button>
                        <span className="text-[#0B2421] font-bold w-4 text-center">{numPlayers}</span>
                        <button
                          onClick={() => setNumPlayers((n) => Math.min(exp.maxPlayers, n + 1))}
                          className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold"
                        >+</button>
                      </div>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="flex items-center justify-between py-3 border-t border-gray-100 mb-4">
                    <span className="text-gray-500 text-sm">Total</span>
                    <span className="text-[#0B2421] font-bold text-xl">SGD {total.toLocaleString()}</span>
                  </div>

                  <button
                    onClick={() => { setView("checkout"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={!selectedDate}
                    className={`w-full py-3.5 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${
                      selectedDate
                        ? "bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] shadow-md"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <Sparkles className="h-4 w-4" />
                    Book This Experience
                  </button>
                  {!selectedDate && (
                    <p className="text-center text-gray-400 text-xs mt-2">Select a date to continue</p>
                  )}
                  <p className="text-center text-gray-400 text-xs mt-3">Free cancellation up to 48 hours before</p>
                </div>
              </div>

            </div>
          </motion.div>
        )}

        {/* ══════════ CHECKOUT VIEW ══════════ */}
        {view === "checkout" && (
          <motion.div key="checkout" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5">

              <div>
                <h2 className="text-[#0B2421] font-bold text-2xl mb-1">Review & Pay</h2>
                <p className="text-gray-400 text-sm">Confirm your booking details before payment</p>
              </div>

              {/* Booking summary */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="relative h-32 overflow-hidden">
                  <img src={exp.image} alt={exp.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2421]/80 to-transparent" />
                  <div className="absolute bottom-3 left-4">
                    <p className="text-white font-bold text-lg">{exp.name}</p>
                    <p className="text-white/70 text-xs">{exp.tagline}</p>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400 flex items-center gap-1.5"><CalendarIcon className="h-3.5 w-3.5" /> Date</span>
                    <span className="text-[#0B2421] font-semibold">{selectedDate ? format(selectedDate, "dd MMM yyyy") : "—"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Guests</span>
                    <span className="text-[#0B2421] font-semibold">{numPlayers} person{numPlayers > 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Duration</span>
                    <span className="text-[#0B2421] font-semibold">{exp.duration}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-gray-100 pt-3">
                    <span className="text-gray-400">SGD {exp.price} per player × {numPlayers} player{numPlayers > 1 ? "s" : ""}</span>
                    <span className="text-[#0B2421] font-bold text-base">SGD {total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* What's included reminder */}
              <div className="bg-[#0B2421]/5 rounded-2xl p-5">
                <p className="text-xs font-bold text-[#0B2421] uppercase tracking-widest mb-3">Included in your booking</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {exp.includes.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment method */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-bold text-[#0B2421] uppercase tracking-widest mb-3">Payment Method</p>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method}
                      onClick={() => setPaymentMethod(method)}
                      className={`flex items-center gap-2 px-3 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                        paymentMethod === method
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B2421]"
                          : "border-gray-200 text-gray-500 hover:border-[#0B2421]/30"
                      }`}
                    >
                      <CreditCard className="h-4 w-4 shrink-0" />
                      {method}
                    </button>
                  ))}
                </div>
              </div>

              {/* Confirm */}
              <button
                onClick={handleConfirm}
                disabled={!paymentMethod || isLoading}
                className={`w-full py-4 rounded-xl font-bold text-base transition-all ${
                  paymentMethod && !isLoading
                    ? "bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] shadow-md"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isLoading ? "Processing..." : `Confirm & Pay SGD ${total.toLocaleString()}`}
              </button>
              <p className="text-center text-gray-400 text-xs">Secure payment · Free cancellation up to 48h before</p>
            </div>
          </motion.div>
        )}

        {/* ══════════ CONFIRMATION VIEW ══════════ */}
        {view === "confirmation" && (
          <motion.div
            key="confirmation"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">

              {/* Success hero */}
              <div className="bg-[#0B2421] rounded-3xl px-8 py-10 text-center mb-5 relative overflow-hidden">
                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/5" />
                <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-white/5" />
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 260 }}
                  className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mx-auto mb-5"
                >
                  <CheckCircle2 className="h-10 w-10 text-[#0B2421]" />
                </motion.div>
                <h1 className="text-white font-bold text-3xl mb-2">You're Booked!</h1>
                <p className="text-white/60 text-sm mb-5">Your experience has been confirmed. See you there!</p>
                <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-full px-5 py-2">
                  <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase">Reservation</span>
                  <span className="text-[#D4AF37] font-bold text-sm">{reservation}</span>
                </div>
              </div>

              {/* Summary card */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden mb-5">
                <div className="relative h-28 overflow-hidden">
                  <img src={exp.image} alt={exp.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2421]/80 to-transparent" />
                  <div className="absolute bottom-3 left-4">
                    <p className="text-white font-bold">{exp.name}</p>
                    <p className="text-white/60 text-xs">{exp.tagline}</p>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Date</span>
                    <span className="text-[#0B2421] font-semibold">{selectedDate ? format(selectedDate, "dd MMM yyyy") : "—"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Guests</span>
                    <span className="text-[#0B2421] font-semibold">{numPlayers}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Duration</span>
                    <span className="text-[#0B2421] font-semibold">{exp.duration}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-gray-100 pt-3">
                    <span className="text-gray-400 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> {paymentMethod}</span>
                    <span className="text-[#0B2421] font-bold">SGD {total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/")}
                className="w-full py-4 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold rounded-2xl transition-colors text-base shadow-md"
              >
                Back to Home
              </button>
              <button
                onClick={() => navigate("/exqura-experience")}
                className="w-full py-3 mt-2 text-gray-400 hover:text-[#0B2421] text-sm font-semibold transition-colors"
              >
                Browse More Experiences <ArrowRight className="inline h-3.5 w-3.5 ml-1" />
              </button>
              <p className="text-center text-gray-400 text-xs mt-2">A confirmation email will be sent to you shortly</p>
            </div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
}
